import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";

import { scrollToId } from "@/components/site/SmoothScroll";

const links = [
  { id: "releases", label: "Releases" },
  { id: "journey", label: "Journey" },
  { id: "about", label: "About" },
  { id: "book", label: "Book" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goTo = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <motion.header
      initial={{ y: -60, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay: 1.2, duration: 1, ease: [0.16, 1, 0.3, 1] }}
      data-testid="site-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0a0a0a]/70 border-b border-[color:var(--line)]"
          : "bg-transparent"
      }`}
    >
      <nav className="mx-auto flex h-16 max-w-[1500px] items-center justify-between px-6 md:h-20 md:px-10">
        <button
          data-testid="navbar-logo"
          onClick={() => goTo("hero")}
          className="group flex items-center gap-3"
        >
          <span className="relative flex h-6 w-6 items-center justify-center">
            <span className="absolute inset-0 rounded-full border border-[color:var(--gold)]/60" />
            <span className="absolute inset-1.5 rounded-full bg-[color:var(--gold)]" />
          </span>
          <span className="font-display text-xs md:text-sm font-black tracking-[0.32em] uppercase text-ink">
            Irwan <span className="text-gold">Craw</span>
          </span>
        </button>

        <ul className="hidden md:flex items-center gap-10">
          {links.map((l) => (
            <li key={l.id}>
              <button
                data-testid={`navbar-link-${l.id}`}
                onClick={() => goTo(l.id)}
                className="group relative text-[11px] font-semibold uppercase tracking-[0.28em] text-ink/80 hover:text-[color:var(--gold-hi)] transition-colors"
              >
                {l.label}
                <span className="pointer-events-none absolute left-0 -bottom-1.5 block h-px w-0 bg-[color:var(--gold)] transition-all duration-500 group-hover:w-full" />
              </button>
            </li>
          ))}
        </ul>

        <div className="hidden md:block">
          <button
            data-testid="navbar-cta-booking"
            onClick={() => goTo("book")}
            className="group relative inline-flex items-center gap-3 rounded-full border border-[color:var(--gold)]/60 bg-transparent px-5 py-2.5 text-[10px] font-bold uppercase tracking-[0.28em] text-[color:var(--gold-hi)] transition-all duration-700 hover:bg-[color:var(--gold)] hover:text-[#0a0a0a] hover:shadow-[0_0_28px_rgba(201,169,97,0.35),0_0_60px_rgba(201,169,97,0.18)]"
          >
            <span>Book Irwan Craw</span>
            <span className="inline-block h-1 w-1 rounded-full bg-current transition-all duration-500 group-hover:scale-[1.6]" />
          </button>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden inline-flex h-10 w-10 items-center justify-center rounded-full border border-[color:var(--line-strong)] bg-white/5 text-ink backdrop-blur"
          aria-label="Toggle menu"
        >
          {open ? <X size={16} /> : <Menu size={16} />}
        </button>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="navbar-mobile-menu"
            className="md:hidden border-t border-[color:var(--line)] bg-[#0a0a0a]/95 backdrop-blur-xl px-6 py-6 overflow-hidden"
          >
            <ul className="flex flex-col gap-5">
              {links.map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`navbar-mobile-link-${l.id}`}
                    onClick={() => goTo(l.id)}
                    className="w-full text-left text-sm font-semibold uppercase tracking-[0.28em] text-ink/80"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
              <li>
                <button
                  data-testid="navbar-mobile-cta-booking"
                  onClick={() => goTo("book")}
                  className="mt-2 w-full rounded-full border border-[color:var(--gold)]/60 px-5 py-3 text-[10px] font-bold uppercase tracking-[0.28em] text-[color:var(--gold-hi)]"
                >
                  Book Irwan Craw
                </button>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
